import type { Component } from "vue"

import type { GetDisplayLabel } from "@peerdb/peerdb/types"

import { FetchError, getURL } from "@peerdb/peerdb/api"
import RouterLink from "@peerdb/peerdb/components/RouterLink"
import { configKey } from "@peerdb/peerdb/config"
import siteContext from "@peerdb/peerdb/context"
import { NAME, TITLE } from "@peerdb/peerdb/core"
import { D, getBestClaimOfType, getClaimsOfTypeWithConfidence, selectClaimsByLanguage } from "@peerdb/peerdb/document"
import i18n from "@peerdb/peerdb/i18n"
import { progressKey } from "@peerdb/peerdb/progress"
import { registerDisplayLabelFunction } from "@peerdb/peerdb/registry/display-label"
import { creditsDisabled, registerFooterEndComponent, registerFooterStartComponent } from "@peerdb/peerdb/registry/footer"
import { registerHomeComponent } from "@peerdb/peerdb/registry/home"
import { registerNavbarComponent } from "@peerdb/peerdb/registry/navbar"
import routes from "@peerdb/peerdb/routes"
import twMerge from "@peerdb/peerdb/tw-merge"
import { getDisplayLabel } from "@peerdb/peerdb/utils"
import { createApp, ref } from "vue"
import { createRouter, createWebHistory } from "vue-router"

import "@/app.css"
import App from "@/App.vue"
import en from "@/locales/en.json"
import sl from "@/locales/sl.json"
import AboutLink from "@/partials/AboutLink.vue"
import AccessibilityStatementLink from "@/partials/AccessibilityStatementLink.vue"
import Credits from "@/partials/Credits.vue"
import Home from "@/partials/Home.vue"
import NavBarLinks from "@/partials/NavBarLinks.vue"
import OpenSourceLink from "@/partials/OpenSourceLink.vue"
import PrivacyPolicyLink from "@/partials/PrivacyPolicyLink.vue"
import StatisticsLink from "@/partials/StatisticsLink.vue"
import TermsOfUseLink from "@/partials/TermsOfUseLink.vue"
import {
  ART_GROUP,
  ARTIST_INDIVIDUAL,
  ASSOCIATION,
  AUTHOR_GROUP,
  AUTHOR_INDIVIDUAL,
  BIBLIOGRAPHIC_UNIT,
  COUNTRY,
  EVENT,
  EXHIBITION,
  EXHIBITION_RESPONSE,
  EXHIBITION_VENUE,
  INSTITUTION,
  MEDIA,
  ORGANISER,
  PROFESSOR,
  PUBLIC_ARTWORK,
  RENOWNED_PERSON,
  SETTLEMENT,
  SOURCE_FROM_EXHIBITION,
  SYMPOSIUM_OR_ART_COLONY,
  TRAVEL_OR_ARTIST_RESIDENCY,
} from "./classes"
import {
  CONTAINED_IN,
  DATE_OF_BIRTH,
  DATE_OF_FORMATION,
  DISPLAY_NOTE,
  HAS_ARTIST,
  HAS_AUTHOR,
  HAS_LOCATION,
  HAS_MEDIA,
  HAS_MEDIA_CATEGORY,
  HAS_PUBLIC_ARTWORK_CATEGORY,
  HAS_RAZUME_MEDIA_TYPE,
  LAST_NAME,
  LOCATION_OF_BIRTH,
  LOCATION_OF_FORMATION,
  PERIOD,
  PUBLISHED,
} from "./properties"

// During development when requests are proxied to Vite, placeholders
// in HTML files are not rendered. So we set them here as well.
if (siteContext.title) {
  document.title = siteContext.title
}

// Match the per-view entry modules but not the SFC script sub-chunks the lib emits alongside them (named like Home.vue_vue_type_script_setup_true_lang.js).
const peerdbViews = import.meta.glob<{ default: Component }>(["/node_modules/@peerdb/peerdb/lib/views/*.js", "!/node_modules/@peerdb/peerdb/lib/views/*.vue_*.js"])

const localRoutes = [
  {
    path: "/statistics",
    name: "Statistics",
    component: () => import("@/views/Statistics.vue"),
    props: true,
    strict: true,
  },
]

const localAPIRoutes = [
  {
    path: "/api/statistics",
    name: "Statistics",
    component: () => null,
    props: true,
    strict: true,
  },
]

const router = createRouter({
  history: createWebHistory(),
  scrollBehavior(to, from, savedPosition) {
    // Search route handles its own scrolling through "at" query parameter.
    if (to.name === "SearchGet") {
      return false
    }
    // Hash navigation is handled by component-level watchers (e.g. the edit view table of contents)
    // so they can apply navbar-aware offsets and smooth scrolling.
    if (to.hash) {
      return false
    }
    // Same-page navigations that only change the hash or query (e.g. the table of contents
    // scroll-spy replacing the hash, or a tab switch) must preserve the current scroll position,
    // otherwise the scroll-spy fights the user by yanking the page back to the top. path excludes
    // the query and hash, so an equal path means the same route and params.
    if (to.path === from.path) {
      return false
    }
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  },
  routes: [
    ...Object.entries(routes)
      .filter(([, route]) => route.handlers)
      .map(([name, route]) => ({
        path: route.path,
        name,
        component: peerdbViews[`/node_modules/@peerdb/peerdb/lib/views/${name}.js`],
        props: true,
        strict: true,
      })),
    // localRoutes have to come after PeerDB routes, otherwise they get overwritten.
    ...localRoutes,
  ],
})

const apiRouter = createRouter({
  history: createWebHistory(),
  routes: [
    ...Object.entries(routes)
      .filter(([, route]) => route.api)
      .map(([name, route]) => ({
        path: route.path === "/" ? "/api" : `/api${route.path}`,
        name,
        component: () => null,
        props: true,
        strict: true,
      })),
    // localAPIRoutes have to come after PeerDB routes, otherwise they get overwritten.
    ...localAPIRoutes,
  ],
})

router.apiResolve = apiRouter.resolve.bind(apiRouter)

const app = createApp(App).use(router)

// We replace Vue Router's RouterLink with ours.
delete app._context.components["RouterLink"]
app.component("RouterLink", RouterLink)

i18n.global.mergeLocaleMessage("en", en)
i18n.global.mergeLocaleMessage("sl", sl)

registerNavbarComponent(NavBarLinks)

registerHomeComponent(Home)

registerFooterStartComponent(AboutLink)
registerFooterStartComponent(TermsOfUseLink)
registerFooterStartComponent(PrivacyPolicyLink)
registerFooterStartComponent(AccessibilityStatementLink)
registerFooterStartComponent(OpenSourceLink)
registerFooterStartComponent(StatisticsLink)

registerFooterEndComponent(Credits)
creditsDisabled.value = true

// bestString picks the string claim with the highest confidence among those whose
// language matches the given locale (with the language priority chain). Mirrors the
// Go-side bestString template helper, which uses SelectClaimsByLanguage.
function bestString(claims: Parameters<GetDisplayLabel>[0], propID: string, locale: string): string | null {
  const selected = selectClaimsByLanguage(claims, "string", propID, locale, (cs) => cs.length > 0 && !!cs[0].string)
  return selected?.[0].string ?? null
}

// fetchClaims fetches a referenced document and returns its claims. It returns null when the document is
// not there to be read: when abortSignal aborts, and when the visitor may not read the document. A caller
// cannot tell those two apart, nor apart from a document without claims, so a caller which has to checks
// abortSignal itself after the call.
//
// A document the visitor may not read is answered with 403 and reported as no claims rather than as a
// failure, so that a label naming such a document renders the way one naming nothing renders. This is what
// the label templates in internal/razume/classes.go do with the same reference: they build the label of a
// visibility level out of the documents readable at that level (bestReferenceDoc), so a reference which is
// not readable contributes nothing. Failing here instead would render the whole label of a document the
// visitor may perfectly well read as an error, and would disagree with the label stored for it.
async function fetchClaims(id: string, router: Parameters<GetDisplayLabel>[1], abortSignal: AbortSignal): Promise<Parameters<GetDisplayLabel>[0]> {
  let response
  try {
    response = await getURL<object>(
      router.apiResolve({
        name: "DocumentGet",
        params: {
          id,
        },
      }).href,
      null,
      abortSignal,
      null,
    )
  } catch (error) {
    if (error instanceof FetchError && error.status === 403) {
      return null
    }
    throw error
  }
  // getURL returns null only on abort.
  if (abortSignal.aborted || response === null) {
    return null
  }

  return new D(response.doc).claims
}

const personDisplayLabel: GetDisplayLabel = async function (claims, router, i18n, el, abortSignal, progress) {
  if (!claims) {
    return null
  }

  const { t, locale, fallbackLocale } = i18n
  // With this we register reactive dependency on the internationalization
  // configuration, before we do async get below.
  locale.value // eslint-disable-line @typescript-eslint/no-unused-expressions
  fallbackLocale.value // eslint-disable-line @typescript-eslint/no-unused-expressions

  const name = bestString(claims, NAME, locale.value)
  const lastName = bestString(claims, LAST_NAME, locale.value)
  const displayNote = bestString(claims, DISPLAY_NOTE, locale.value)
  const dateOfBirth = getBestClaimOfType(claims, "time", DATE_OF_BIRTH)?.time
  const locationOfBirthTo = getBestClaimOfType(claims, "ref", LOCATION_OF_BIRTH)?.to

  let locationOfBirth: string | null = null
  if (locationOfBirthTo) {
    const locationClaims = await fetchClaims(locationOfBirthTo.id, router, abortSignal)
    if (abortSignal.aborted) {
      return null
    }

    locationOfBirth = await getDisplayLabel(locationClaims, router, i18n, el, abortSignal, progress)
    if (abortSignal.aborted) {
      return null
    }
  }

  let yearOfBirth: string | null = null
  if (dateOfBirth) {
    yearOfBirth = dateOfBirth.split("-")[0]
  }

  let label: string | null = null
  if (name && lastName) {
    label = `${name} ${lastName}`
  } else if (name) {
    label = name
  } else if (lastName) {
    label = lastName
  }

  if (label) {
    if (yearOfBirth && locationOfBirth && displayNote) {
      label += " " + t("displayLabel.yearLocationOfBirthDisplayNote", { yearOfBirth, locationOfBirth, displayNote })
    } else if (yearOfBirth && displayNote) {
      label += " " + t("displayLabel.yearOfBirthDisplayNote", { yearOfBirth, displayNote })
    } else if (locationOfBirth && displayNote) {
      label += " " + t("displayLabel.locationOfBirthDisplayNote", { locationOfBirth, displayNote })
    } else if (yearOfBirth && locationOfBirth) {
      label += " " + t("displayLabel.yearLocationOfBirth", { yearOfBirth, locationOfBirth })
    } else if (yearOfBirth) {
      label += " " + t("displayLabel.yearOfBirth", { yearOfBirth })
    } else if (locationOfBirth) {
      label += " " + t("displayLabel.locationOfBirth", { locationOfBirth })
    } else if (displayNote) {
      label += " " + t("displayLabel.displayNote", { displayNote })
    }
  }

  return label
}

const groupDisplayLabel: GetDisplayLabel = async function (claims, router, i18n, el, abortSignal, progress) {
  if (!claims) {
    return null
  }

  const { t, locale, fallbackLocale } = i18n
  // With this we register reactive dependency on the internationalization
  // configuration, before we do async get below.
  locale.value // eslint-disable-line @typescript-eslint/no-unused-expressions
  fallbackLocale.value // eslint-disable-line @typescript-eslint/no-unused-expressions

  const name = bestString(claims, NAME, locale.value)
  const displayNote = bestString(claims, DISPLAY_NOTE, locale.value)
  const dateOfFormation = getBestClaimOfType(claims, "time", DATE_OF_FORMATION)?.time
  const locationOfFormationTo = getBestClaimOfType(claims, "ref", LOCATION_OF_FORMATION)?.to

  let locationOfFormation: string | null = null
  if (locationOfFormationTo) {
    const locationClaims = await fetchClaims(locationOfFormationTo.id, router, abortSignal)
    if (abortSignal.aborted) {
      return null
    }

    locationOfFormation = await getDisplayLabel(locationClaims, router, i18n, el, abortSignal, progress)
    if (abortSignal.aborted) {
      return null
    }
  }

  let yearOfFormation: string | null = null
  if (dateOfFormation) {
    yearOfFormation = dateOfFormation.split("-")[0]
  }

  if (!name) {
    return null
  }

  let label = name
  if (yearOfFormation && locationOfFormation && displayNote) {
    label += " " + t("displayLabel.groupYearLocationOfFormationDisplayNote", { yearOfFormation, locationOfFormation, displayNote })
  } else if (yearOfFormation && displayNote) {
    label += " " + t("displayLabel.groupYearOfFormationDisplayNote", { yearOfFormation, displayNote })
  } else if (locationOfFormation && displayNote) {
    label += " " + t("displayLabel.groupLocationOfFormationDisplayNote", { locationOfFormation, displayNote })
  } else if (yearOfFormation && locationOfFormation) {
    label += " " + t("displayLabel.groupYearLocationOfFormation", { yearOfFormation, locationOfFormation })
  } else if (yearOfFormation) {
    label += " " + t("displayLabel.groupYearOfFormation", { yearOfFormation })
  } else if (locationOfFormation) {
    label += " " + t("displayLabel.groupLocationOfFormation", { locationOfFormation })
  } else if (displayNote) {
    label += " " + t("displayLabel.displayNote", { displayNote })
  }

  return label
}

// renderLocationChain mirrors the Go-side locationLabel template: it walks
// CONTAINED_IN up to remaining segments deep, returning "Name, ContainedIn, ..."
// or just "Name" at the chain end or when the depth cap is hit.
async function renderLocationChain(
  claims: Parameters<GetDisplayLabel>[0],
  router: Parameters<GetDisplayLabel>[1],
  i18n: Parameters<GetDisplayLabel>[2],
  el: Parameters<GetDisplayLabel>[3],
  abortSignal: Parameters<GetDisplayLabel>[4],
  progress: Parameters<GetDisplayLabel>[5],
  remaining: number,
): Promise<string | null> {
  if (!claims || remaining <= 0) {
    return null
  }

  const { t, locale } = i18n
  const name = bestString(claims, NAME, locale.value)
  if (!name) {
    return null
  }
  if (remaining <= 1) {
    return name
  }

  const containedInTo = getBestClaimOfType(claims, "ref", CONTAINED_IN)?.to
  if (!containedInTo) {
    return name
  }

  const containedInClaims = await fetchClaims(containedInTo.id, router, abortSignal)
  if (abortSignal.aborted) {
    return null
  }

  const containedIn = await renderLocationChain(containedInClaims, router, i18n, el, abortSignal, progress, remaining - 1)
  if (abortSignal.aborted) {
    return null
  }
  if (containedIn) {
    return t("displayLabel.location", { name, containedIn })
  }
  return name
}

const locationLabel: GetDisplayLabel = async function (claims, router, i18n, el, abortSignal, progress) {
  const { locale, fallbackLocale } = i18n
  // Register reactive dependency on the internationalization configuration
  // before we do async work below.
  locale.value // eslint-disable-line @typescript-eslint/no-unused-expressions
  fallbackLocale.value // eslint-disable-line @typescript-eslint/no-unused-expressions

  return renderLocationChain(claims, router, i18n, el, abortSignal, progress, 3)
}

const organisationLabel: GetDisplayLabel = async function (claims, router, i18n, el, abortSignal, progress) {
  if (!claims) {
    return null
  }

  const { t, locale, fallbackLocale } = i18n
  // With this we register reactive dependency on the internationalization
  // configuration, before we do async get below.
  locale.value // eslint-disable-line @typescript-eslint/no-unused-expressions
  fallbackLocale.value // eslint-disable-line @typescript-eslint/no-unused-expressions

  const name = bestString(claims, NAME, locale.value)

  if (!name) {
    return null
  }

  const locationTo = getBestClaimOfType(claims, "ref", HAS_LOCATION)?.to

  let location: string | null = null
  if (locationTo) {
    const locationClaims = await fetchClaims(locationTo.id, router, abortSignal)
    if (abortSignal.aborted) {
      return null
    }

    location = await getDisplayLabel(locationClaims, router, i18n, el, abortSignal, progress)
    if (abortSignal.aborted) {
      return null
    }
  }

  if (location) {
    return t("displayLabel.organisation", { name, location })
  }

  return name
}

function intervalStartYear(claim: { from?: string } | null | undefined): string | null {
  return claim?.from?.split("-")[0] ?? null
}

const eventLabel: GetDisplayLabel = async function (claims, router, i18n, el, abortSignal, progress) {
  if (!claims) {
    return null
  }

  const { t, locale, fallbackLocale } = i18n
  // With this we register reactive dependency on the internationalization
  // configuration, before we do async get below.
  locale.value // eslint-disable-line @typescript-eslint/no-unused-expressions
  fallbackLocale.value // eslint-disable-line @typescript-eslint/no-unused-expressions

  const title = bestString(claims, TITLE, locale.value)

  // Pick the HAS_LOCATION claim whose PERIOD sub-claim has the earliest From.
  // Fall back to the first claim if no claim has a period.
  const locationClaims = getClaimsOfTypeWithConfidence(claims, "ref", HAS_LOCATION)
  let chosen: (typeof locationClaims)[number] | null = null
  let chosenFrom: string | undefined
  for (const lc of locationClaims) {
    const period = getBestClaimOfType(lc.sub, "timeInterval", PERIOD)
    if (!period?.from) {
      continue
    }
    if (chosenFrom === undefined || period.from < chosenFrom) {
      chosen = lc
      chosenFrom = period.from
    }
  }
  if (!chosen && locationClaims.length > 0) {
    chosen = locationClaims[0]
  }

  let location: string | null = null
  let period: string | null = null
  if (chosen) {
    const locationClaims = await fetchClaims(chosen.to.id, router, abortSignal)
    if (abortSignal.aborted) {
      return null
    }
    location = await getDisplayLabel(locationClaims, router, i18n, el, abortSignal, progress)
    if (abortSignal.aborted) {
      return null
    }
    period = intervalStartYear(getBestClaimOfType(chosen.sub, "timeInterval", PERIOD))
  }

  if (title && location && period) {
    return t("displayLabel.eventTitleLocationPeriod", { title, location, period })
  }
  if (title && location) {
    return t("displayLabel.eventTitleLocation", { title, location })
  }
  if (title) {
    return title
  }
  if (location && period) {
    return t("displayLabel.eventLocationPeriod", { location, period })
  }
  if (location) {
    return location
  }
  return null
}

// nameLabel renders a referenced person or group (an artist or author) as "Name LastName"
// (last name is empty for groups, so it renders just the name). It mirrors the backend nameLabel.
async function nameLabel(id: string, router: Parameters<GetDisplayLabel>[1], locale: string, abortSignal: AbortSignal): Promise<string | null> {
  const claims = await fetchClaims(id, router, abortSignal)
  if (abortSignal.aborted) {
    return null
  }
  const name = bestString(claims, NAME, locale)
  const lastName = bestString(claims, LAST_NAME, locale)
  if (name && lastName) {
    return `${name} ${lastName}`
  }
  if (name) {
    return name
  }
  return lastName
}

// withMore appends the " +N" suffix when a multi-valued reference has additional values
// beyond the one shown, where N is the count minus one. The backend mirrors this with the
// "more" template. It returns the value unchanged for a single value.
//
// TODO: Add better logic to pick which claim to show versus which to show just as a count.
function withMore(value: string, count: number, i18n: Parameters<GetDisplayLabel>[2]): string {
  return count > 1 ? value + i18n.t("displayLabel.andMore", { count: count - 1 }) : value
}

const residencyLabel: GetDisplayLabel = async function (claims, router, i18n, el, abortSignal, progress) {
  if (!claims) {
    return null
  }

  const { t, locale, fallbackLocale } = i18n
  locale.value // eslint-disable-line @typescript-eslint/no-unused-expressions
  fallbackLocale.value // eslint-disable-line @typescript-eslint/no-unused-expressions

  const name = bestString(claims, NAME, locale.value)
  if (name) {
    return name
  }

  const artistTo = getBestClaimOfType(claims, "ref", HAS_ARTIST)?.to
  if (!artistTo) {
    return null
  }
  let artist = await nameLabel(artistTo.id, router, locale.value, abortSignal)
  if (abortSignal.aborted || !artist) {
    return null
  }
  artist = withMore(artist, getClaimsOfTypeWithConfidence(claims, "ref", HAS_ARTIST).length, i18n)

  let location: string | null = null
  const locationTo = getBestClaimOfType(claims, "ref", HAS_LOCATION)?.to
  if (locationTo) {
    const locClaims = await fetchClaims(locationTo.id, router, abortSignal)
    if (abortSignal.aborted) {
      return null
    }
    location = await getDisplayLabel(locClaims, router, i18n, el, abortSignal, progress)
    if (abortSignal.aborted) {
      return null
    }
    if (location) {
      location = withMore(location, getClaimsOfTypeWithConfidence(claims, "ref", HAS_LOCATION).length, i18n)
    }
  }

  const period = intervalStartYear(getBestClaimOfType(claims, "timeInterval", PERIOD))

  if (location && period) {
    return t("displayLabel.residencyArtistLocationPeriod", { artist, location, period })
  }
  if (location) {
    return t("displayLabel.residencyArtistLocation", { artist, location })
  }
  if (period) {
    return t("displayLabel.residencyArtistPeriod", { artist, period })
  }
  return artist
}

const resourceLabel: GetDisplayLabel = async function (claims, router, i18n, el, abortSignal, progress) {
  if (!claims) {
    return null
  }

  const { t, locale, fallbackLocale } = i18n
  locale.value // eslint-disable-line @typescript-eslint/no-unused-expressions
  fallbackLocale.value // eslint-disable-line @typescript-eslint/no-unused-expressions

  const title = bestString(claims, TITLE, locale.value)
  if (title) {
    return title
  }

  let author: string | null = null
  const authorTo = getBestClaimOfType(claims, "ref", HAS_AUTHOR)?.to
  if (authorTo) {
    author = await nameLabel(authorTo.id, router, locale.value, abortSignal)
    if (abortSignal.aborted) {
      return null
    }
    if (author) {
      author = withMore(author, getClaimsOfTypeWithConfidence(claims, "ref", HAS_AUTHOR).length, i18n)
    }
  }

  let media: string | null = null
  const mediaTo = getBestClaimOfType(claims, "ref", HAS_MEDIA)?.to
  if (mediaTo) {
    const mediaClaims = await fetchClaims(mediaTo.id, router, abortSignal)
    if (abortSignal.aborted) {
      return null
    }
    media = await getDisplayLabel(mediaClaims, router, i18n, el, abortSignal, progress)
    if (abortSignal.aborted) {
      return null
    }
    if (media) {
      media = withMore(media, getClaimsOfTypeWithConfidence(claims, "ref", HAS_MEDIA).length, i18n)
    }
  }

  const publishedTime = getBestClaimOfType(claims, "time", PUBLISHED)?.time
  const published = publishedTime ? publishedTime.split("-")[0] : null

  if (author) {
    if (media && published) {
      return t("displayLabel.resourceAuthorMediaPeriod", { author, media, published })
    }
    if (media) {
      return t("displayLabel.resourceAuthorMedia", { author, media })
    }
    if (published) {
      return t("displayLabel.resourceAuthorPeriod", { author, published })
    }
    return author
  }
  if (media) {
    if (published) {
      return t("displayLabel.resourceMediaPeriod", { media, published })
    }
    return media
  }
  return null
}

const publicArtworkLabel: GetDisplayLabel = async function (claims, router, i18n, el, abortSignal, progress) {
  if (!claims) {
    return null
  }

  const { t, locale, fallbackLocale } = i18n
  locale.value // eslint-disable-line @typescript-eslint/no-unused-expressions
  fallbackLocale.value // eslint-disable-line @typescript-eslint/no-unused-expressions

  const title = bestString(claims, TITLE, locale.value)
  if (title) {
    return title
  }

  let category: string | null = null
  const categoryTo = getBestClaimOfType(claims, "ref", HAS_PUBLIC_ARTWORK_CATEGORY)?.to
  if (categoryTo) {
    const categoryClaims = await fetchClaims(categoryTo.id, router, abortSignal)
    if (abortSignal.aborted) {
      return null
    }
    category = await getDisplayLabel(categoryClaims, router, i18n, el, abortSignal, progress)
    if (abortSignal.aborted) {
      return null
    }
  }

  let artist: string | null = null
  const artistTo = getBestClaimOfType(claims, "ref", HAS_ARTIST)?.to
  if (artistTo) {
    artist = await nameLabel(artistTo.id, router, locale.value, abortSignal)
    if (abortSignal.aborted) {
      return null
    }
    if (artist) {
      artist = withMore(artist, getClaimsOfTypeWithConfidence(claims, "ref", HAS_ARTIST).length, i18n)
    }
  }

  if (category && artist) {
    let location: string | null = null
    const locationTo = getBestClaimOfType(claims, "ref", HAS_LOCATION)?.to
    if (locationTo) {
      const locClaims = await fetchClaims(locationTo.id, router, abortSignal)
      if (abortSignal.aborted) {
        return null
      }
      location = await getDisplayLabel(locClaims, router, i18n, el, abortSignal, progress)
      if (abortSignal.aborted) {
        return null
      }
    }

    const period = intervalStartYear(getBestClaimOfType(claims, "timeInterval", PERIOD))

    if (location && period) {
      return t("displayLabel.artworkCategoryArtistLocationPeriod", { category, artist, location, period })
    }
    if (location) {
      return t("displayLabel.artworkCategoryArtistLocation", { category, artist, location })
    }
    if (period) {
      return t("displayLabel.artworkCategoryArtistPeriod", { category, artist, period })
    }
    return t("displayLabel.artworkCategoryArtist", { category, artist })
  }
  if (artist) {
    return artist
  }
  return category
}

const mediaLabel: GetDisplayLabel = async function (claims, router, i18n, el, abortSignal, progress) {
  if (!claims) {
    return null
  }

  const { t, locale, fallbackLocale } = i18n
  locale.value // eslint-disable-line @typescript-eslint/no-unused-expressions
  fallbackLocale.value // eslint-disable-line @typescript-eslint/no-unused-expressions

  const name = bestString(claims, NAME, locale.value)
  if (!name) {
    return null
  }

  // The media type is not assumed to be present even though the field is required.
  let type: string | null = null
  const typeTo = getBestClaimOfType(claims, "ref", HAS_RAZUME_MEDIA_TYPE)?.to
  if (typeTo) {
    const typeClaims = await fetchClaims(typeTo.id, router, abortSignal)
    if (abortSignal.aborted) {
      return null
    }
    type = await getDisplayLabel(typeClaims, router, i18n, el, abortSignal, progress)
    if (abortSignal.aborted) {
      return null
    }
  }

  let category: string | null = null
  const categoryTo = getBestClaimOfType(claims, "ref", HAS_MEDIA_CATEGORY)?.to
  if (categoryTo) {
    const categoryClaims = await fetchClaims(categoryTo.id, router, abortSignal)
    if (abortSignal.aborted) {
      return null
    }
    category = await getDisplayLabel(categoryClaims, router, i18n, el, abortSignal, progress)
    if (abortSignal.aborted) {
      return null
    }
  }

  if (type && category) {
    return t("displayLabel.mediaNameTypeCategory", { name, type, category })
  }
  if (type) {
    return t("displayLabel.mediaNameType", { name, type })
  }
  if (category) {
    return t("displayLabel.mediaNameCategory", { name, category })
  }
  return name
}

registerDisplayLabelFunction(ARTIST_INDIVIDUAL, personDisplayLabel)
registerDisplayLabelFunction(AUTHOR_INDIVIDUAL, personDisplayLabel)
registerDisplayLabelFunction(RENOWNED_PERSON, personDisplayLabel)
registerDisplayLabelFunction(PROFESSOR, personDisplayLabel)
registerDisplayLabelFunction(SETTLEMENT, locationLabel)
registerDisplayLabelFunction(COUNTRY, locationLabel)
registerDisplayLabelFunction(EXHIBITION_VENUE, locationLabel)
registerDisplayLabelFunction(INSTITUTION, organisationLabel)
registerDisplayLabelFunction(ORGANISER, organisationLabel)
registerDisplayLabelFunction(ASSOCIATION, organisationLabel)
registerDisplayLabelFunction(EVENT, eventLabel)
registerDisplayLabelFunction(SYMPOSIUM_OR_ART_COLONY, eventLabel)
registerDisplayLabelFunction(EXHIBITION, eventLabel)
registerDisplayLabelFunction(ART_GROUP, groupDisplayLabel)
registerDisplayLabelFunction(AUTHOR_GROUP, groupDisplayLabel)
registerDisplayLabelFunction(TRAVEL_OR_ARTIST_RESIDENCY, residencyLabel)
registerDisplayLabelFunction(BIBLIOGRAPHIC_UNIT, resourceLabel)
registerDisplayLabelFunction(EXHIBITION_RESPONSE, resourceLabel)
registerDisplayLabelFunction(SOURCE_FROM_EXHIBITION, resourceLabel)
registerDisplayLabelFunction(PUBLIC_ARTWORK, publicArtworkLabel)
registerDisplayLabelFunction(MEDIA, mediaLabel)

app
  .use(i18n)
  .use(twMerge)
  .provide(progressKey, ref(0))
  .provide(
    configKey,
    ref({
      fixedNavbar: false,
    }),
  )
  .mount("main")
